import {
  addDays,
  formatHoursMinutes,
  getJerusalemParts,
  isIsraelDst,
  toJerusalemDateKey
} from './date';
import { hourFromCompactTime, minuteFromCompactTime } from './time';

const ZMAN_PRESENTATION = {
  alot: { label: 'עלות השחר', icon: 'sunrise' },
  netz: { label: 'נץ החמה', icon: 'sunrise' },
  sz_kriat_shma: { label: 'סוף זמן קריאת שמע', icon: 'book' },
  sz_tfila: { label: 'סוף זמן תפילה', icon: 'book' },
  hatzot: { label: 'חצות היום', icon: 'sun' },
  sunset: { label: 'שקיעה', icon: 'sunset' },
  stars: { label: 'צאת הכוכבים', icon: 'night' },
  candles: { label: 'הדלקת נרות', icon: 'candles' },
  havdalah: { label: 'מוצאי שבת', icon: 'havdalah' },
  fastStart: { label: 'תחילת הצום', icon: 'hourglass' },
  fastEnd: { label: 'סוף הצום', icon: 'coffee' }
};

const FAST_DEFINITIONS = [
  { key: 'gedaliah', label: 'צום גדליה', matches: (name) => name.includes('גדליה') },
  { key: 'tevet', label: 'עשרה בטבת', matches: (name) => name.includes('עשרה בטבת') },
  { key: 'esther', label: 'תענית אסתר', matches: (name) => name.includes('תענית אסתר') },
  {
    key: 'tammuz',
    label: 'י״ז בתמוז',
    matches: (name) => name.includes('תמוז') && (name.includes('צום') || name.includes('יז'))
  },
  { key: 'tisha-bav', label: 'תשעה באב', matches: (name) => name.includes('תשעה באב') }
];

function normalizeHebrewName(value = '') {
  return String(value)
    .replace(/[\u0591-\u05BD\u05BF\u05C1\u05C2\u05C4\u05C5\u05C7]/g, '')
    .replace(/["'׳״]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function holidayNamesForDay(day) {
  const eventNames = (day?.events || [])
    .filter((event) => event?.category === 'holiday')
    .map((event) => event.hebrew || event.title || event.name || '');
  const sharedNames = Array.isArray(day?.sharedCalendarDay?.holidays)
    ? day.sharedCalendarDay.holidays
    : [];

  return [...eventNames, ...sharedNames].map(normalizeHebrewName).filter(Boolean);
}

function findFastDefinition(day) {
  const names = holidayNamesForDay(day);
  return FAST_DEFINITIONS.find((definition) => names.some(definition.matches)) || null;
}

function holidayNamesMatch(left, right) {
  // Shared-calendar names can use newer Hebcal spellings than the legacy catalog.
  // Exact aliases avoid treating eves, Chol Hamoed or Yom Kippur Katan as Yom Tov.
  const aliases = {
    'יום כיפור': 'יום כפור',
    'יום הכיפורים': 'יום כפור',
    'סוכות א': 'סוכות יום א',
    'פסח א': 'פסח יום א',
    'פסח ז': 'פסח יום ז',
    'שמחת תורה': 'שמיני עצרת'
  };
  const canonicalName = (name) => {
    const withoutYear = name.replace(/\s+\d{4}$/, '');
    return aliases[withoutYear] || withoutYear;
  };
  return canonicalName(left) === canonicalName(right);
}

function isYomTovDay(day, holidays) {
  // Prefer the calendar's explicit flag over matching a translated display name.
  if (day?.events?.some((event) => event?.category === 'holiday' && event.yomtov === true)) {
    return true;
  }
  const yomTovNames = (holidays || [])
    .filter((holiday) => holiday?.yomTov === '1')
    .flatMap((holiday) => [holiday.apiName, holiday.name].map((name) => normalizeHebrewName(name)))
    .filter(Boolean);
  const dayNames = holidayNamesForDay(day);

  return dayNames.some((dayName) =>
    yomTovNames.some((holidayName) => holidayNamesMatch(dayName, holidayName))
  );
}

function isHolyDay(day, holidays) {
  return Boolean(day) && (day.weekday === 6 || isYomTovDay(day, holidays));
}

function formatCompactZman(day, index) {
  const raw = day?.zmanim?.[index];
  if (raw == null || raw === '') {
    return '';
  }

  const hours =
    hourFromCompactTime(raw) +
    (!day.zmanimResolved && isIsraelDst(day.dateKey) ? 1 : 0);
  return formatHoursMinutes(hours, minuteFromCompactTime(raw));
}

function formatEventTime(event) {
  if (!event?.date) {
    return '';
  }

  const parts = getJerusalemParts(event.date);
  return formatHoursMinutes(parts.hours, parts.minutes);
}

function createTile(index, time, overrides = {}) {
  if (!time) {
    return null;
  }

  return {
    key: overrides.key || index,
    index,
    time,
    ...ZMAN_PRESENTATION[index],
    ...overrides
  };
}

function createDayTile(day, index, overrides) {
  return createTile(index, formatCompactZman(day, index), {
    dateKey: day?.dateKey || '',
    ...overrides
  });
}

function createPair(key, items, kind = 'regular') {
  const visibleItems = items.filter(Boolean);
  return visibleItems.length ? { key, kind, items: visibleItems } : null;
}

function eventDateKey(event, fallbackDateKey) {
  return toJerusalemDateKey(event?.date) || fallbackDateKey || '';
}

function collectHolyBlocks(days) {
  const events = days
    .flatMap((day) =>
      (day?.events || [])
        .filter((event) => event?.category === 'candles' || event?.category === 'havdalah')
        .map((event) => ({
          event,
          type: event.category,
          dateKey: eventDateKey(event, day.dateKey),
          timestamp: new Date(event.date || `${day.dateKey}T12:00:00Z`).getTime()
        }))
    )
    .sort((left, right) => left.timestamp - right.timestamp);

  const blocks = [];
  let openBlock = null;

  events.forEach((entry) => {
    if (entry.type === 'candles') {
      if (!openBlock) {
        openBlock = { startDateKey: entry.dateKey, candles: [] };
      }
      openBlock.candles.push(entry);
      return;
    }

    if (openBlock) {
      blocks.push({
        ...openBlock,
        endDateKey: entry.dateKey,
        havdalah: entry
      });
      openBlock = null;
    }
  });

  return blocks;
}

function buildEventHolyPair(days, byDate, holidays, todayDateKey) {
  const block = collectHolyBlocks(days).find(
    (candidate) =>
      candidate.startDateKey <= todayDateKey && todayDateKey <= candidate.endDateKey
  );
  if (!block) {
    return null;
  }

  const candle = [...block.candles]
    .reverse()
    .find((entry) => entry.dateKey <= todayDateKey);
  if (!candle) {
    return null;
  }

  const endDay = byDate.get(block.endDateKey);
  const exitLabel = isYomTovDay(endDay, holidays) ? 'מוצאי חג' : 'מוצאי שבת';
  const exitTime = formatCompactZman(endDay, 'havdalah') || formatEventTime(block.havdalah.event);

  return createPair(
    `holy-${block.startDateKey}-${block.endDateKey}`,
    [
      createTile('candles', formatEventTime(candle.event), {
        key: `candles-${candle.dateKey}`,
        dateKey: candle.dateKey
      }),
      createTile('havdalah', exitTime, {
        key: `havdalah-${block.endDateKey}`,
        dateKey: block.endDateKey,
        label: exitLabel
      })
    ],
    'holy'
  );
}

function buildFallbackHolyPair(byDate, holidays, todayDateKey) {
  const today = byDate.get(todayDateKey);
  const tomorrowDateKey = addDays(todayDateKey, 1);
  const tomorrow = byDate.get(tomorrowDateKey);
  const todayIsHoly = isHolyDay(today, holidays);
  const tomorrowIsHoly = isHolyDay(tomorrow, holidays);

  if (!todayIsHoly && !tomorrowIsHoly) {
    return null;
  }

  let endDateKey = todayIsHoly ? todayDateKey : tomorrowDateKey;
  while (isHolyDay(byDate.get(addDays(endDateKey, 1)), holidays)) {
    endDateKey = addDays(endDateKey, 1);
  }

  const candleDay = todayIsHoly && !tomorrowIsHoly
    ? byDate.get(addDays(todayDateKey, -1))
    : today;
  const endDay = byDate.get(endDateKey);

  return createPair(
    `holy-fallback-${todayDateKey}-${endDateKey}`,
    [
      createDayTile(candleDay, 'candles'),
      createDayTile(endDay, 'havdalah', {
        label: isYomTovDay(endDay, holidays) ? 'מוצאי חג' : 'מוצאי שבת'
      })
    ],
    'holy'
  );
}

function buildFastPair(fastDay, fastDefinition, byDate, todayDateKey) {
  const isPreview = fastDay.dateKey !== todayDateKey;
  const fastStartDay = fastDefinition.key === 'tisha-bav'
    ? byDate.get(addDays(fastDay.dateKey, -1))
    : fastDay;
  const fastStartIndex = fastDefinition.key === 'tisha-bav' ? 'sunset' : 'fast';
  const contextLabel = isPreview ? 'מחר' : '';

  return createPair(
    `fast-${fastDefinition.key}-${fastDay.dateKey}`,
    [
      createTile('fastStart', formatCompactZman(fastStartDay, fastStartIndex), {
        key: `fast-start-${fastDay.dateKey}`,
        dateKey: fastDay.dateKey,
        contextLabel
      }),
      createTile('fastEnd', formatCompactZman(fastDay, 'stars'), {
        key: `fast-end-${fastDay.dateKey}`,
        dateKey: fastDay.dateKey,
        contextLabel
      })
    ],
    'fast'
  );
}

export function buildZmanimDisplayModel(data, now = Date.now()) {
  const todayDateKey = getJerusalemParts(now).dateKey;
  const sourceDays = [
    ...(Array.isArray(data?.zmanimContextDays) ? data.zmanimContextDays : []),
    ...(Array.isArray(data?.days) ? data.days : [])
  ];
  const byDate = new Map(sourceDays.filter((day) => day?.dateKey).map((day) => [day.dateKey, day]));
  const days = [...byDate.values()].sort((left, right) => left.dateKey.localeCompare(right.dateKey));
  const today = byDate.get(todayDateKey) || data?.days?.[0] || null;
  const effectiveTodayDateKey = today?.dateKey || todayDateKey;
  const tomorrow = byDate.get(addDays(effectiveTodayDateKey, 1));
  const fastDay = [today, tomorrow].find((day) => findFastDefinition(day)) || null;
  const fastDefinition = fastDay ? findFastDefinition(fastDay) : null;
  const presentationDay = fastDefinition ? fastDay : today;
  const holidays = Array.isArray(data?.holidays) ? data.holidays : [];
  const holyPair =
    buildEventHolyPair(days, byDate, holidays, effectiveTodayDateKey) ||
    buildFallbackHolyPair(byDate, holidays, effectiveTodayDateKey);
  const pairs = [
    createPair('morning-light', [
      createDayTile(presentationDay, 'alot'),
      createDayTile(presentationDay, 'netz')
    ]),
    createPair('morning-deadlines', [
      createDayTile(presentationDay, 'sz_kriat_shma'),
      createDayTile(presentationDay, 'sz_tfila')
    ]),
    createPair(
      fastDefinition ? 'fast-daylight' : 'evening',
      fastDefinition
        ? [createDayTile(presentationDay, 'hatzot'), createDayTile(presentationDay, 'sunset')]
        : [
            createDayTile(presentationDay, 'sunset'),
            holyPair ? null : createDayTile(presentationDay, 'stars')
          ]
    )
  ];

  if (fastDay && fastDefinition) {
    pairs.push(buildFastPair(fastDay, fastDefinition, byDate, effectiveTodayDateKey));
  }

  if (holyPair) {
    pairs.push(holyPair);
  }

  return {
    dateKey: effectiveTodayDateKey,
    fast: fastDefinition
      ? {
          key: fastDefinition.key,
          label: fastDefinition.label,
          dateKey: fastDay.dateKey,
          isPreview: fastDay.dateKey !== effectiveTodayDateKey
        }
      : null,
    pairs: pairs.filter(Boolean)
  };
}
